import { useEffect, useMemo, useRef } from "react";
import type { IUser } from "@/core/auth";
import {
  FOX_SONS_BUSINESS_NAME,
  getFoxSonsVisibleTechIds,
} from "@/icm/constants/fox-sons-access-control";
import { useGetIcmJobs, useGetIcmStatements } from "@/icm/query";
import { PAY_BREAKDOWN_RUN_TARGET_TYPE } from "@/payroll/pay-breakdown/types";
import type { PayBreakdownPeriodState } from "./use-pay-breakdown-period";

type UseIcmOutputParams = {
  businessId: string | null;
  user: IUser | null;
} & Pick<PayBreakdownPeriodState, "business" | "output" | "hasActiveRun">;

export const useIcmOutput = ({
  businessId,
  user,
  business,
  output,
  hasActiveRun,
}: UseIcmOutputParams) => {
  const selectedIcmOutputRunId =
    output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.icm ? output.runId : null;
  const {
    data: statements,
    isLoading: isStatementsLoading,
    refetch: refetchStatements,
  } = useGetIcmStatements(businessId, selectedIcmOutputRunId);
  const { data: jobsData, refetch: refetchJobs } = useGetIcmJobs(
    businessId,
    selectedIcmOutputRunId,
  );
  const jobs = jobsData?.jobs ?? [];

  const isFoxAndSons = business?.name === FOX_SONS_BUSINESS_NAME;
  const foxSonsVisibleTechIds = useMemo(
    () => (isFoxAndSons ? getFoxSonsVisibleTechIds(user?.email ?? "") : null),
    [isFoxAndSons, user?.email],
  );

  const visibleStatements = useMemo(() => {
    const all = statements ?? [];
    if (!foxSonsVisibleTechIds) return all;

    return all.filter(
      (statement) =>
        statement.technician_crm_external_id != null &&
        foxSonsVisibleTechIds.has(statement.technician_crm_external_id),
    );
  }, [statements, foxSonsVisibleTechIds]);

  const visibleJobs = useMemo(() => {
    if (!foxSonsVisibleTechIds) return jobs;

    const allowedStatementIds = new Set(visibleStatements.map((s) => s.id));
    return jobs.filter((job) =>
      job.payouts.some((payout) =>
        allowedStatementIds.has(payout.statement_id),
      ),
    );
  }, [jobs, foxSonsVisibleTechIds, visibleStatements]);

  const hasActiveRunRef = useRef(hasActiveRun);

  useEffect(() => {
    if (!selectedIcmOutputRunId) {
      hasActiveRunRef.current = hasActiveRun;
      return;
    }

    if (hasActiveRunRef.current && !hasActiveRun) {
      refetchStatements();
      refetchJobs();
    }
    hasActiveRunRef.current = hasActiveRun;
  }, [hasActiveRun, refetchJobs, refetchStatements, selectedIcmOutputRunId]);

  return { statements, isStatementsLoading, visibleStatements, visibleJobs };
};

export type IcmOutputState = ReturnType<typeof useIcmOutput>;
